import { useEffect, useState, type FormEvent } from 'react';
import { apiErrorText } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { parseMoney } from '@shared/lib/money';
import {
  Form,
  Row,
  FormGroup,
  Label,
  Input,
  Select,
  ModalFormFooter,
  FieldError,
  FormError,
} from './styled';

interface CreateAdvanceRetentionModalProps {
  open: boolean;
  onClose: () => void;
  /** Сохраняет запись; при отказе сервера бросает ошибку — окно покажет её и сохранит ввод. */
  onCreateAdv: (data: {
    direction: string;
    counterpartyName: string;
    contractNumber: string;
    projectName: string;
    initialAmount: number;
    expectedCloseDate?: string;
  }) => Promise<void>;
  isCreating: boolean;
}

export function CreateAdvanceRetentionModal({
  open,
  onClose,
  onCreateAdv,
  isCreating,
}: CreateAdvanceRetentionModalProps) {
  const [advDirection, setAdvDirection] = useState('payable_advance');
  const [advCpName, setAdvCpName] = useState('');
  const [advContract, setAdvContract] = useState('');
  const [advProject, setAdvProject] = useState('');
  const [advAmount, setAdvAmount] = useState('');
  const [advDueDate, setAdvDueDate] = useState('');
  const [amountError, setAmountError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Окно открыли заново — прежняя ошибка уже не про этот ввод
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isCreating) return;
    const amt = parseMoney(advAmount);
    if (amt === null) {
      setAmountError('Введите сумму, например 2 500 000,00');
      return;
    }
    if (amt <= 0) {
      setAmountError('Сумма должна быть больше нуля');
      return;
    }
    setAmountError(null);
    if (!advCpName.trim()) {
      setError('Укажите контрагента');
      return;
    }

    setError(null);
    try {
      await onCreateAdv({
        direction: advDirection,
        counterpartyName: advCpName.trim(),
        contractNumber: advContract.trim() || 'Без номера',
        projectName: advProject.trim() || 'Общий',
        initialAmount: amt,
        expectedCloseDate: advDueDate || undefined,
      });
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить запись об авансе или удержании'));
      return;
    }
    // Сохранено — очищаем форму для следующей записи
    setAdvCpName('');
    setAdvAmount('');
    setAdvContract('');
    setAdvProject('');
    setAdvDueDate('');
  };

  return (
    <Modal
      open={open}
      title="Новая запись об авансе или гарантийном удержании"
      onClose={onClose}
    >
      <Form onSubmit={handleSubmit}>
        {error && <FormError role="alert">{error}</FormError>}

        <FormGroup>
          <Label htmlFor="adv-direction">Направление операции</Label>
          <Select
            id="adv-direction"
            value={advDirection}
            onChange={(e) => setAdvDirection(e.target.value)}
          >
            <option value="payable_advance">Аванс выданный (подрядчикам/поставщикам)</option>
            <option value="receivable_advance">Аванс полученный (от Заказчика)</option>
            <option value="receivable_retention">Гарантийное удержание Заказчика к возврату</option>
            <option value="payable_retention">Гарантийное удержание с субподрядчика</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="adv-counterparty">Контрагент *</Label>
          <Input
            id="adv-counterparty"
            placeholder="ООО СтройСнаб"
            value={advCpName}
            onChange={(e) => setAdvCpName(e.target.value)}
            required
          />
        </FormGroup>

        <Row>
          <FormGroup>
            <Label htmlFor="adv-project">Объект строительства</Label>
            <Input
              id="adv-project"
              placeholder="ПС 110 кВ Заполярная"
              value={advProject}
              onChange={(e) => setAdvProject(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="adv-contract">Номер договора</Label>
            <Input
              id="adv-contract"
              placeholder="2026/СМР-14"
              value={advContract}
              onChange={(e) => setAdvContract(e.target.value)}
            />
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="adv-amount">Сумма, ₽ *</Label>
            <Input
              id="adv-amount"
              type="text"
              inputMode="decimal"
              placeholder="2 500 000,00"
              value={advAmount}
              onChange={(e) => setAdvAmount(e.target.value)}
              aria-invalid={Boolean(amountError)}
              aria-describedby={amountError ? 'adv-amount-error' : undefined}
              required
            />
            {amountError && <FieldError id="adv-amount-error">{amountError}</FieldError>}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="adv-due-date">Ожидаемый срок закрытия</Label>
            <Input
              id="adv-due-date"
              type="date"
              value={advDueDate}
              onChange={(e) => setAdvDueDate(e.target.value)}
            />
          </FormGroup>
        </Row>

        <ModalFormFooter>
          <Button $variant="secondary" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button $variant="primary" type="submit" disabled={isCreating}>
            {isCreating ? 'Сохранение…' : 'Добавить'}
          </Button>
        </ModalFormFooter>
      </Form>
    </Modal>
  );
}
